import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { PageHeader, Card, Badge, Modal, Btn, Field, inputCls } from "../components/common";
import { toast } from "sonner";
import { RefreshCw, Pencil, Star, Wrench } from "lucide-react";

const STATUS_FLOW = ["baru", "diverifikasi", "dijadwalkan", "diproses", "menunggu_konfirmasi", "selesai", "ditutup"];
const TONE = { baru: "amber", diverifikasi: "cyan", dijadwalkan: "cyan", diproses: "amber", menunggu_konfirmasi: "amber", selesai: "emerald", ditutup: "slate" };

export default function TiketApp() {
  const [rows, setRows] = useState([]);
  const [sla, setSla] = useState({});
  const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ status: "", response: "", assigned_team: "", technician: "", scheduled_at: "" });
  const [pulling, setPulling] = useState(false);

  const load = useCallback(() => {
    api.get("/sales/complaints").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/sales/tiket/sla").then((r) => setSla(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const pull = async () => {
    setPulling(true);
    try { const r = await api.post("/ajl/pull"); toast.success(r.data.skipped ? "URL AJL belum diset" : "Penarikan selesai"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setPulling(false); }
  };

  const openEdit = (c) => {
    setForm({ status: c.status, response: c.response || "", assigned_team: c.assigned_team || "", technician: c.technician || "", scheduled_at: (c.scheduled_at || "").slice(0, 16), repair_notes: c.repair_notes || "" });
    setEdit(c);
  };
  const [upKind, setUpKind] = useState(null);
  const uploadPhotos = async (c, kind, files) => {
    if (!files?.length) return;
    const fd = new FormData();
    fd.append("kind", kind);
    [...files].forEach((f) => fd.append("files", f));
    setUpKind(kind);
    try { await api.post(`/sales/complaints/${c.id}/repair-photos`, fd); toast.success(`Foto ${kind} terunggah`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setUpKind(null); }
  };
  const save = () => api.put(`/sales/complaints/${edit.id}`, form)
    .then(() => { toast.success("Tiket diperbarui & konsumen diberi notifikasi"); setEdit(null); load(); })
    .catch((e) => toast.error(errMsg(e)));

  const filtered = status ? rows.filter((r) => r.status === status) : rows;
  return (
    <div data-testid="tiket-app-page">
      <PageHeader title="Tiket Aplikasi (Ajukan Perbaikan)" subtitle="Tiket layanan & komplain konsumen — timeline status tersinkron ke aplikasi" testid="tiket-header">
        <Btn onClick={pull} disabled={pulling} data-testid="tiket-pull-btn"><RefreshCw className={`w-4 h-4 ${pulling ? "animate-spin" : ""}`} /> Tarik sekarang</Btn>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[["Respons rata-rata", sla.avg_response_hours != null ? `${sla.avg_response_hours} jam` : "-", "sla-resp"],
          ["Penyelesaian rata-rata", sla.avg_resolution_hours != null ? `${sla.avg_resolution_hours} jam` : "-", "sla-res"],
          ["Rating konsumen", sla.avg_rating != null ? `${sla.avg_rating}/5 (${sla.rated_count})` : "-", "sla-rating"],
          ["Total tiket", rows.length, "sla-total"]].map(([l, v, k]) => (
          <Card key={k} testid={k}><p className="text-[11px] text-slate-500">{l}</p><p className="text-lg font-semibold text-slate-200 mt-0.5">{v}</p></Card>
        ))}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {["", ...STATUS_FLOW].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`tiket-filter-${s || "semua"}`}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${status === s ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900"}`}>
            {s ? s.replace(/_/g, " ") : "Semua"}
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="tiket-list">
        {filtered.length === 0 && <Card><p className="text-slate-500 text-sm">Tidak ada tiket.</p></Card>}
        {filtered.map((c) => (
          <Card key={c.id} className="!p-4" testid={`tiket-${c.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Wrench className="w-4 h-4 text-amber-400 shrink-0" />
              <span className="font-medium text-slate-200">{c.customer_name}</span>
              <Badge tone="cyan">{c.unit_label || "-"}</Badge>
              <Badge tone={TONE[c.status] || "slate"}>{(c.status || "").replace(/_/g, " ")}</Badge>
              <Badge tone="slate">{c.category || "umum"}</Badge>
              {(c.priority || "").toLowerCase() === "darurat" && <Badge tone="rose">DARURAT</Badge>}
              {c.rating && <span className="text-amber-300 text-xs flex items-center gap-0.5"><Star className="w-3 h-3" />{c.rating}</span>}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <label className="text-[10px] text-cyan-300 cursor-pointer hover:underline" data-testid={`tiket-upbefore-${c.id}`}>
                  {upKind === "before" ? "mengunggah..." : `+ Foto Sebelum (${(c.photos_before || []).length}/6)`}
                  <input type="file" className="hidden" accept="image/*" multiple onChange={(e) => uploadPhotos(c, "before", e.target.files)} />
                </label>
                <label className="text-[10px] text-emerald-300 cursor-pointer hover:underline" data-testid={`tiket-upafter-${c.id}`}>
                  {upKind === "after" ? "mengunggah..." : `+ Foto Sesudah (${(c.photos_after || []).length}/6)`}
                  <input type="file" className="hidden" accept="image/*" multiple onChange={(e) => uploadPhotos(c, "after", e.target.files)} />
                </label>
                {c.completed_at && <span className="text-[10px] text-slate-500">selesai: {String(c.completed_at).replace("T", " ").slice(0, 16)}</span>}
              </div>
              <button onClick={() => openEdit(c)} className="ml-auto text-slate-400 hover:text-emerald-300" data-testid={`tiket-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-slate-400 mt-1.5">{c.message}</p>
            <div className="text-[11px] text-slate-500 mt-1">
              {c.source} · {(c.created_at || "").replace("T", " ").slice(0, 16)}
              {c.assigned_team && ` · tim: ${c.assigned_team}`}{c.technician && ` · teknisi: ${c.technician}`}
              {c.scheduled_at && ` · jadwal: ${String(c.scheduled_at).replace("T", " ").slice(0, 16)}`}
            </div>
            {c.response && <p className="text-[11px] text-emerald-300/80 mt-1">Tanggapan: {c.response}</p>}
          </Card>
        ))}
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Tiket — ${edit?.customer_name} (${edit?.unit_label || "-"})`} testid="tiket-modal">
        <div className="space-y-3">
          <Field label="Status">
            <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="tiket-status">
              {STATUS_FLOW.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Tanggapan untuk konsumen">
            <textarea className={inputCls} rows={2} value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} data-testid="tiket-response" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Tim penanggung jawab"><input className={inputCls} value={form.assigned_team} onChange={(e) => setForm({ ...form, assigned_team: e.target.value })} data-testid="tiket-team" /></Field>
            <Field label="Teknisi"><input className={inputCls} value={form.technician} onChange={(e) => setForm({ ...form, technician: e.target.value })} data-testid="tiket-tech" /></Field>
          </div>
          <Field label="Catatan pekerjaan (repair notes)">
            <textarea className={inputCls} rows={2} value={form.repair_notes} onChange={(e) => setForm({ ...form, repair_notes: e.target.value })} placeholder="Contoh: Pipa wastafel diganti, sudah dites tidak bocor" data-testid="tiket-repair-notes" />
          </Field>
          <Field label="Jadwal kunjungan teknisi">
            <input type="datetime-local" className={inputCls} value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="tiket-schedule" />
          </Field>
          <Btn onClick={save} data-testid="tiket-save">Simpan & Beri Tahu Konsumen</Btn>
        </div>
      </Modal>
    </div>
  );
}
